// Phase 486 Plan 03 — SC#3 token-diff probe (D-486-08).
//
// This is renderer INFRASTRUCTURE, NOT a user-facing demo feature: the route
// is unlinked (no nav, no landing card) and exists solely so the Playwright
// token-diff.spec.ts can prove that two distinct `designTokens` sets render
// the SAME page tree visibly differently — the ROADMAP SC#3 wording.
//
// It renders a small fixed probe tree built from arbitrary-value Tailwind
// classes that read the design-token CSS variables directly
// (`bg-[var(--color-background)]`, `text-[var(--color-foreground)]`,
// `bg-[var(--color-primary)]`, ...). `getIsolatedAppStyle(themeTokens)` emits
// every `--color-*` variable on the SchemaV4PageRenderer wrapper div, so
// swapping `themeTokens.colors` changes the rendered colors. Arbitrary-value
// utilities are generated by Tailwind v4 whenever it scans the source — they
// do not depend on a shadcn `@theme` mapping, so the probe is deterministic.
//
// The token set is selected by the `?variant=a|b` query param so a single
// route serves both screenshots. No on-demo theme toggle (D-486-08).
import {
  SchemaV4PageRenderer,
  type SchemaV4Page,
  type SchemaV4PageThemeTokens,
} from "@cogo/react-renderer/adapters/schema-v4-page";

// A fixed probe tree — every color class reads a design-token CSS variable
// via an arbitrary-value utility, so the token overlay is directly visible.
const PROBE_PAGE: SchemaV4Page = {
  schemaVersion: "4.0",
  blockType: "token-diff-probe",
  root: {
    id: "token-probe-root",
    type: "container",
    className:
      "flex flex-col gap-4 p-12 min-h-screen bg-[var(--color-background)] text-[var(--color-foreground)]",
    layout: { type: "flex", direction: "column", gap: "16" },
    children: [
      {
        id: "probe-headline",
        type: "headline",
        text: "Token Diff Probe",
        weight: "extrabold",
        className: "text-4xl font-extrabold text-[var(--color-primary)]",
      },
      {
        id: "probe-card",
        type: "container",
        className:
          "flex flex-col gap-2 p-8 rounded-xl bg-[var(--color-primary)] text-[var(--color-primary-foreground)]",
        layout: { type: "flex", direction: "column", gap: "8" },
        children: [
          {
            id: "probe-card-text",
            type: "text",
            text: "This card paints from the primary design token.",
            weight: "semibold",
            className: "text-lg font-semibold",
          },
        ],
      },
      {
        id: "probe-accent",
        type: "container",
        className:
          "p-6 rounded-lg bg-[var(--color-accent)] text-[var(--color-accent-foreground)]",
        children: [
          {
            id: "probe-accent-text",
            type: "text",
            text: "Accent token surface.",
            weight: "medium",
            className: "text-base font-medium",
          },
        ],
      },
    ],
  },
};

// Two visibly-distinct design-token sets. Variant A leaves the default
// shadcn light theme; variant B overlays a saturated palette so the probe
// tree paints noticeably differently.
const VARIANT_A: SchemaV4PageThemeTokens = undefined;

const VARIANT_B: SchemaV4PageThemeTokens = {
  colors: {
    background: "#0b1120",
    foreground: "#e2e8f0",
    primary: "#f97316",
    "primary-foreground": "#1c1917",
    accent: "#7c3aed",
    "accent-foreground": "#faf5ff",
  },
};

export function TokenDiffProbe() {
  const variant =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("variant")
      : null;
  const themeTokens = variant === "b" ? VARIANT_B : VARIANT_A;
  return (
    <main data-token-variant={variant === "b" ? "b" : "a"}>
      <SchemaV4PageRenderer page={PROBE_PAGE} themeTokens={themeTokens} />
    </main>
  );
}
